import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import io, { Socket } from "socket.io-client";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import {
  ROLE,
  SOCET_CONNECTION_URL,
  SOCKET_CONNECTION_PATH,
} from "../helpers/utils";
import { getDecryptedToken, getDecryptedUserData } from "../helpers/bycrypt";
import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";
import { AppLifecycle, isAppLifecycleAvailable } from "../plugins/appLifecycle";
import { trackDeviceCloseAndWait } from "../helpers/deviceTracking";

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  userOnlineStatus: Map<string, boolean>;
  setUserOnlineStatus: React.Dispatch<
    React.SetStateAction<Map<string, boolean>>
  >;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [userOnlineStatus, setUserOnlineStatus] = useState<
    Map<string, boolean>
  >(new Map());
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  const { user } = useSelector((state: RootState) => state.auth);
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!user) {
      socketRef.current?.disconnect();
      socketRef.current = null;

      setSocket(null);
      setIsConnected(false);
      setTotalUnreadCount(0);
      setUserOnlineStatus(new Map());
    }
  }, [user]);

  useEffect(() => {
    const token = getDecryptedToken();

    if (!token || !user) {
      // console.log("❌ No token or user found for socket connection");
      return;
    }

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Don't create a new connection if one already exists and is connected
    if (socketRef.current?.connected) {
      // console.log("🔌 Socket already connected, reusing existing connection");
      return;
    }

    // console.log(
    //   "🔌 Initializing socket connection with token:",
    //   token?.substring(0, 20) + "..."
    // );
    // const SOCET_CONNECTION_URL = "";
    // const SOCKET_CONNECTION_PATH = "";
    const newSocket = io(SOCET_CONNECTION_URL, {
      path: SOCKET_CONNECTION_PATH,
      transports: ["websocket"],
      auth: {
        token: `${token}`,
      },
      reconnection: true,
      // reconnectionAttempts: 10,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    // Connection events
    newSocket.on("connect", () => {
      console.log("🟢 Connected to socket server", newSocket.id);
      setIsConnected(true);
      setSocket(newSocket);
      socketRef.current = newSocket;

      newSocket.emit("get_online_users");
      // newSocket.emit("get_total_unread");
    });

    newSocket.on("connect_error", (err) => {
      console.log("❌ Connection error:", err);
      setIsConnected(false);
    });

    newSocket.on("disconnect", (reason) => {
      console.log("🔌 Socket disconnected:", reason);
      setIsConnected(false);
      // setTotalUnreadCount(0); // Reset unread count on disconnect

      // Auto-reconnect after a delay if disconnected unexpectedly
      if (reason === "io server disconnect") {
        // Server initiated disconnect, try to reconnect
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log("🔄 Attempting to reconnect...");
          newSocket.connect();
        }, 2000);
      }
    });

    newSocket.on("online_users", (users: string[]) => {
      const map = new Map();
      users.forEach((id) => map.set(id, true));
      setUserOnlineStatus(map);
    });

    newSocket.on("restart-app", async () => {
      const userData = getDecryptedUserData();
      const roleKey = userData?.role?.key || "";
      const roleName = String(userData?.role?.name || "").toLowerCase();
      const isAttendanceTabletRole =
        roleKey === ROLE.ATTENDANCE_TABLET ||
        roleKey === "attendance-tablet" ||
        roleName.includes("attendance tablet") ||
        roleName.includes("attendance-tablet");

      if (
        !isAttendanceTabletRole ||
        !Capacitor.isNativePlatform() ||
        Capacitor.getPlatform() !== "android" ||
        !isAppLifecycleAvailable()
      ) {
        return;
      }

      try {
        // Report the close and give the request a moment to complete before the
        // process is killed (device tracking).
        await trackDeviceCloseAndWait();
        await AppLifecycle.restartApp({ delayMs: 1500, wakeScreen: true });
      } catch (error) {
        console.error("SocketContext: restartApp failed:", error);
      }
    });

    // Chat room events
    newSocket.on("joined_chat", (data) => {
      // console.log("✅ Successfully joined chat:", data);
    });

    newSocket.on("left_chat", (data) => {
      // console.log("👋 Successfully left chat:", data);
    });

    newSocket.on("set_local_unique_mail_id", (data: any) => {
      console.log("📥 Received set_local_unique_mail_id event:", data);
      const roomId =
        typeof data === "string"
          ? data
          : data?.target_room || data?.room || data?.unique_id || data?.id;
      if (roomId) {
        localStorage.setItem("sm_target_room", roomId);
        console.log("💾 Stored sm_target_room in localStorage:", roomId);
      }
    });

    newSocket.on("clear_local_unique_mail_id", (data: any) => {
      console.log("📥 Received clear_local_unique_mail_id event:", data);
      const currentRoom = localStorage.getItem("sm_target_room");
      const targetRoom =
        typeof data === "string"
          ? data
          : data?.target_room || data?.room || data?.unique_id || data?.id;

      if (!targetRoom || !currentRoom || targetRoom === currentRoom) {
        localStorage.removeItem("sm_target_room");
        console.log("🧹 Removed sm_target_room from localStorage");
      }
    });

    newSocket.on("error", (error) => {
      console.error("❌ Socket error:", error);
    });

    const handleOnline = async () => {
      console.log("🌐 Window Online Event Fired");
      if (newSocket && newSocket.disconnected) {
        console.log("🔌 Reconnecting socket from window online...");
        newSocket.connect();
      }
    };
    const handleOffline = () => {
      console.log("🌐 Window Offline Event Fired");
      setIsConnected(false);
      if (newSocket && newSocket.connected) {
        newSocket.disconnect();
      }
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    let networkListener: any;
    if (Capacitor.isNativePlatform()) {
      Network.addListener("networkStatusChange", (status) => {
        console.log("📡 Native Network Status Changed:", status);
        if (status.connected && newSocket) {
          if (newSocket.disconnected) {
            console.log("🔌 Reconnecting socket from native network event...");
            newSocket.connect();
          } else {
            console.log("🔌 Socket already connected, but network changed. Forcing reconnect just in case.");
            newSocket.disconnect();
            setTimeout(() => { newSocket.connect(); }, 500);
          }
        } else if (!status.connected) {
          setIsConnected(false);
          if (newSocket && newSocket.connected) {
            console.log("🔌 Disconnecting socket due to offline network...");
            newSocket.disconnect();
          }
        }
      }).then((listener) => {
        networkListener = listener;
      });
    }

    // Capacitor backgrounding or weird network drops can permanently break Socket.io's built-in reconnection timers.
    // This interval aggressively forces a reconnect if the socket is unexpectedly disconnected.
    const aggressiveReconnectInterval = setInterval(async () => {
      let isOnline = navigator.onLine;
      if (Capacitor.isNativePlatform()) {
        try {
          const status = await Network.getStatus();
          isOnline = status.connected;
        } catch (e) {}
      }
      
      if (isOnline && newSocket && newSocket.disconnected && user) {
        console.log("🔄 Aggressively forcing socket reconnect...");
        newSocket.connect();
      }
    }, 5000);

    // Cleanup function
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      clearInterval(aggressiveReconnectInterval);

      if (newSocket) {
        // console.log("🔌 Cleaning up socket connection");
        newSocket.removeAllListeners();
        newSocket.disconnect();
      }

      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (networkListener) {
        networkListener.remove();
      }

      setSocket(null);
      setIsConnected(false);
      socketRef.current = null;
    };
  }, [user?._id]); // Only reconnect when user changes

  // Socket utility functions

  const value: SocketContextType = {
    socket,
    isConnected,
    userOnlineStatus,
    setUserOnlineStatus,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
